import React, { useState, useMemo, useEffect } from 'react'
import { toastUtil } from '../utils/toast'

interface CustomCalendarProps {
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  closedDates: string[] // YYYY-MM-DD 형식의 휴일 리스트
  maxDate?: Date // 선택 가능한 최대 날짜 (미래일자 선택 불가)
  closedDateMessage?: string // 휴일 선택 시 표시할 메시지
  futureDateMessage?: string // 미래 날짜 선택 시 표시할 메시지
  dateSize?: number // 일자 버튼 크기 (기본값: 40)
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedDate,
  onDateSelect,
  closedDates,
  maxDate,
  closedDateMessage,
  futureDateMessage,
  dateSize = 40,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(
    null
  )

  const monthNames = [
    '1월',
    '2월',
    '3월',
    '4월',
    '5월',
    '6월',
    '7월',
    '8월',
    '9월',
    '10월',
    '11월',
    '12월',
  ]

  const weekDays = ['일', '월', '화', '수', '목', '금', '토']

  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days: (number | null)[] = []

    // 이전 달의 마지막 날짜들
    const prevMonthLastDay = new Date(year, month, 0).getDate()
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(prevMonthLastDay - i)
    }

    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    // 다음 달의 날짜들 (캘린더를 채우기 위해)
    const remainingDays = 42 - days.length
    for (let day = 1; day <= remainingDays; day++) {
      days.push(day)
    }

    return days
  }, [currentMonth])

  useEffect(() => {
    if (slideDirection) {
      const timer = setTimeout(() => {
        setSlideDirection(null)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [slideDirection])

  const handlePrevMonth = () => {
    setSlideDirection('right')
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    )
  }

  const handleNextMonth = () => {
    setSlideDirection('left')
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    )
  }

  const isClosedDate = (year: number, month: number, day: number): boolean => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      day
    ).padStart(2, '0')}`
    return closedDates.includes(dateStr)
  }

  const isFutureDate = (year: number, month: number, day: number): boolean => {
    if (!maxDate) return false

    const date = new Date(year, month, day)
    date.setHours(0, 0, 0, 0)
    const maxDateNormalized = new Date(maxDate)
    maxDateNormalized.setHours(0, 0, 0, 0)
    return date > maxDateNormalized
  }

  const isSelectedDate = (
    year: number,
    month: number,
    day: number
  ): boolean => {
    if (!selectedDate) return false
    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    )
  }

  const handleDateClick = (day: number, year: number, month: number) => {
    if (day === null) return

    const currentYear = currentMonth.getFullYear()
    const currentMonthIndex = currentMonth.getMonth()

    // 다른 달의 날짜를 클릭한 경우 해당 달로 이동
    if (year !== currentYear || month !== currentMonthIndex) {
      setCurrentMonth(new Date(year, month, 1))
      // 달 이동 후 날짜 선택 처리
      setTimeout(() => {
        if (isClosedDate(year, month, day)) {
          if (closedDateMessage) {
            toastUtil.error(closedDateMessage)
          }
          return
        }

        if (isFutureDate(year, month, day)) {
          if (futureDateMessage) {
            toastUtil.error(futureDateMessage)
          }
          return
        }

        const date = new Date(year, month, day)
        onDateSelect(date)
      }, 100)
      return
    }

    if (isClosedDate(year, month, day)) {
      if (closedDateMessage) {
        toastUtil.error(closedDateMessage)
      }
      return
    }

    if (isFutureDate(year, month, day)) {
      if (futureDateMessage) {
        toastUtil.error(futureDateMessage)
      }
      return
    }

    const date = new Date(year, month, day)
    onDateSelect(date)
  }

  const isCurrentMonth = (index: number, day: number | null): boolean => {
    if (day === null) return false

    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const startingDayOfWeek = firstDay.getDay()
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()

    // 현재 달의 날짜들만 true
    return index >= startingDayOfWeek && index < startingDayOfWeek + daysInMonth
  }

  const getActualDateInfo = (index: number, day: number | null) => {
    if (day === null) return null

    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const startingDayOfWeek = firstDay.getDay()
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()

    // 이전 달의 날짜들
    if (index < startingDayOfWeek) {
      const prevMonth = month === 0 ? 11 : month - 1
      const prevYear = month === 0 ? year - 1 : year
      return { year: prevYear, month: prevMonth, day }
    }

    // 현재 달의 날짜들
    if (index < startingDayOfWeek + daysInMonth) {
      return { year, month, day }
    }

    // 다음 달의 날짜들
    const nextMonth = month === 11 ? 0 : month + 1
    const nextYear = month === 11 ? year + 1 : year
    const nextMonthLastDay = new Date(nextYear, nextMonth + 1, 0).getDate()

    // 다음 달의 실제 날짜 범위를 넘어서면 null 반환
    if (day > nextMonthLastDay) {
      return null
    }

    return { year: nextYear, month: nextMonth, day }
  }

  return (
    <div className="w-full flex flex-col justify-stretch">
      {/* 월 네비게이션 */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="flex w-24px h-24px hover:opacity-70"
        >
          <img
            src="/images/month-back-arrow-icon.png"
            alt="이전 달"
            className="w-full h-full object-contain"
          />
        </button>

        <span className="flex-1 flex items-center justify-center font-pretendard font-700 text-16px text-brown-80%-main text-center">
          {monthNames[currentMonth.getMonth()]}
        </span>

        <button
          type="button"
          onClick={handleNextMonth}
          className="flex w-24px h-24px hover:opacity-70"
        >
          <img
            src="/images/month-forward-arrow-icon.png"
            alt="다음 달"
            className="w-full h-full object-contain"
          />
        </button>
      </div>

      {/* 요일 헤더 */}
      <div className="mt-16px grid grid-cols-7">
        {weekDays.map((day, index) => (
          <div
            key={day}
            className={`
              flex
              items-center
              justify-center
            `}
            style={{
              width: `${dateSize}px`,
              height: `${dateSize}px`,
            }}
          >
            <span
              className={`
              font-pretendard font-700 text-14px text-center leading-20px
              ${
                index === 0
                  ? 'text-[#B40508CC]'
                  : index === 6
                    ? 'text-186EBA-80%'
                    : 'text-textColorText2'
              }
            `}
            >
              {day}
            </span>
          </div>
        ))}
      </div>

      {/* 캘린더 그리드 */}
      <div className="mt-16px relative overflow-hidden">
        <div
          key={`${currentMonth.getFullYear()}-${currentMonth.getMonth()}`}
          className={`
            grid
            grid-cols-7
            transition-all
            duration-300
            ease-in-out
            ${slideDirection === 'left' ? 'animate-slide-left' : ''}
            ${slideDirection === 'right' ? 'animate-slide-right' : ''}
          `}
        >
          {calendarData.map((day, index) => {
            const isCurrent = isCurrentMonth(index, day)
            const dateInfo = getActualDateInfo(index, day)

            if (day === null || !dateInfo) {
              return (
                <div
                  key={`empty-${index}`}
                  className="flex items-center justify-center"
                  style={{
                    width: `${dateSize}px`,
                    height: `${dateSize}px`,
                  }}
                />
              )
            }

            const { year, month } = dateInfo

            const isClosed = isClosedDate(year, month, day)
            const isFuture = isFutureDate(year, month, day)
            const isSelected = isSelectedDate(year, month, day)
            const isSundayColumn = index % 7 === 0

            const isClickable = !isClosed && !isFuture

            return (
              <button
                key={`${year}-${month}-${day}-${index}`}
                type="button"
                onClick={() => handleDateClick(day, year, month)}
                style={{
                  width: `${dateSize}px`,
                  height: `${dateSize}px`,
                }}
                className={`
                  flex
                  items-center
                  justify-center
                  rounded-full
                  transition-all
                  ${isSelected ? 'bg-[#6E5C4A]' : ''}
                  ${
                    !isSelected && isClickable ? 'hover:bg-brandPrimary-5%' : ''
                  }
                  ${isClosed ? 'cursor-not-allowed' : ''}
                  ${isFuture ? 'cursor-not-allowed opacity-50' : ''}
                  ${
                    !isCurrent
                      ? 'text-textColorText3 opacity-40'
                      : !isClosed && !isFuture && !isSelected
                        ? 'text-textColorText2'
                        : ''
                  }
                  ${!isClickable ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <span
                  className={`
                    transition-all
                    ${
                      isSelected
                        ? 'text-style-button2 text-white'
                        : `text-style-captionM ${
                            isClosed
                              ? 'text-semanticError'
                              : isFuture
                                ? 'text-textColorText3'
                                : isSundayColumn
                                  ? 'text-[#E16868]'
                                  : ''
                          }`
                    }
                  `}
                >
                  {day}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CustomCalendar
